
import React, { useEffect, useState } from "react";
import { FaBell, FaUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from '../config/api';

interface User {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  username?: string;
  contact?: string;
  status?: string;
  role?: string;
}

interface Document {
  id: number;
  file_name: string;
  file_path?: string;
  description?: string;
  uploaded_by?: string;
  uploaded_at: string;
  is_archived?: boolean;
  evaluation_id?: number | null;
}

export default function OperatorDashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [assignedUsers, setAssignedUsers] = useState<User[]>([]);
  const [activeSection, setActiveSection] = useState<'profile' | 'users' | 'notifications'>('profile');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [modalUser, setModalUser] = useState<User | null>(null);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [file, setFile] = useState<File | null>(null);
  // Notification form state
  const [selectedUserId, setSelectedUserId] = useState<number | 'ALL' | null>(null);
  const [notifSubject, setNotifSubject] = useState('');
  const [notifText, setNotifText] = useState('');
  const [sendingNotif, setSendingNotif] = useState(false);
  const [notifMessage, setNotifMessage] = useState<string | null>(null);
  const [sendToAllAssigned, setSendToAllAssigned] = useState(false);
  const [notifProgress, setNotifProgress] = useState<{sent:number,total:number}>({sent:0,total:0});

  useEffect(() => {
    const operatorData = localStorage.getItem("operatorUser");
    if (operatorData) {
      setProfile(JSON.parse(operatorData));
    } else {
      navigate("/operatorLogin");
    }
  }, [navigate]);

  useEffect(() => {
    if (!profile) return;
    fetch(`${API_BASE_URL}/users/${profile.id}`)
      .then(res => res.json())
      .then(data => {
        if (data.user && data.user.assigned_users) {
          const ids = data.user.assigned_users.split(',').map((id: string) => id.trim()).filter(Boolean);
          Promise.all(ids.map((id: string) =>
            fetch(`${API_BASE_URL}/users/${id}`).then(res => res.json()).then(u => u.user)
          )).then(setAssignedUsers);
        }
      });
  }, [profile]);

  // Set default selected user when assignedUsers loads
  useEffect(() => {
    if (assignedUsers.length > 0 && selectedUserId === null) {
      setSelectedUserId(assignedUsers[0].id);
    }
  }, [assignedUsers, selectedUserId]);

  const handleViewProfile = (user: User) => {
    setModalUser(user);
    setShowProfileModal(true);
  };

  const handleViewDocuments = (user: User) => {
    setModalUser(user);
    fetch(`${API_BASE_URL}/documents/user/${user.id}`)
      .then(res => res.json())
      .then(data => {
        setDocuments(data.documents || []);
        setShowDocsModal(true);
      });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files ? e.target.files[0] : null);
  };

  const handleUpload = async () => {
    if (!file || !modalUser) return;
    setUploading(true);
    setUploadError('');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', String(modalUser.id));
    try {
      const res = await fetch(`${API_BASE_URL}/documents/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setDocuments(prev => [...prev, data.document]);
        setFile(null);
      } else {
        setUploadError(data.error || 'Upload failed');
      }
    } catch {
      setUploadError('Server error');
    }
    setUploading(false);
  };

  return (
    <div className="flex h-screen">
      {/* Side Menu */}
      <aside className="w-64 bg-gray-900 text-white flex flex-col shadow-lg">
        <div className="flex flex-col items-center justify-center py-8">
          <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Logo" className="h-auto w-[90%] mb-2 shadow" />
          {/* <span className="font-bold text-xl tracking-wide">HT Immigration</span> */}
        </div>
        <div className="bg-blue-900 px-6 py-4 border-b border-gray-800">
          <span className="font-bold text-xl tracking-wide ">Operator Dashboard</span>
        </div>
        <nav className="flex-1 px-4 py-8">
          <ul className="space-y-2">
            <li
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-semibold cursor-pointer transition-colors ${activeSection === 'profile' ? 'bg-blue-600 text-white' : 'hover:bg-gray-800 hover:text-blue-400 text-gray-200'}`}
              onClick={() => setActiveSection('profile')}
            >
              <span className="material-icons"><FaUser /></span> Profile
            </li>
            <li
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-semibold cursor-pointer transition-colors ${activeSection === 'users' ? 'bg-blue-600 text-white' : 'hover:bg-gray-800 hover:text-blue-400 text-gray-200'}`}
              onClick={() => setActiveSection('users')}
            >
              <span className="material-icons"><FaUser /></span> Users
            </li>
            <li
              className={`flex items-center gap-2 px-4 py-3 rounded-lg font-semibold cursor-pointer transition-colors ${activeSection === 'notifications' ? 'bg-blue-600 text-white' : 'hover:bg-gray-800 hover:text-blue-400 text-gray-200'}`}
              onClick={() => setActiveSection('notifications')}
            >
              <span className="material-icons"><FaBell /></span> Notifications
            </li>
          </ul>
        </nav>
      </aside>
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="flex items-center justify-end bg-white shadow p-4">
          <div className="flex items-center gap-4">
            <span>{profile?.first_name} {profile?.last_name}</span>
            <button
              className="bg-gray-900 text-white px-3 py-1 rounded"
              onClick={() => setShowLogoutConfirm(true)}
            >
              Logout
            </button>
        {/* Logout Confirmation Modal */}
        {showLogoutConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm relative">
              <h3 className="text-xl font-bold mb-4 text-center">Confirm Logout</h3>
              <p className="mb-6 text-center">Are you sure you want to logout?</p>
              <div className="flex justify-center gap-4">
                <button
                  className="bg-red-600 text-white px-4 py-2 rounded font-semibold"
                  onClick={() => { setShowLogoutConfirm(false); localStorage.removeItem("operatorUser"); navigate("/operatorLogin"); }}
                >
                  Yes, Logout
                </button>
                <button
                  className="bg-gray-300 text-gray-800 px-4 py-2 rounded font-semibold"
                  onClick={() => setShowLogoutConfirm(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
          </div>
        </header>
        <main className="flex-1 p-0 bg-gray-50">
          {activeSection === 'profile' && (
            <section className="mt-12 flex justify-center">
              <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md">
                <h2 className="text-2xl font-bold mb-6 text-center text-[#0097b2]">Operator Profile</h2>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Username:</span>
                    <span>{profile?.username}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Name:</span>
                    <span>{profile?.first_name} {profile?.last_name}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Email:</span>
                    <span>{profile?.email}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Role:</span>
                    <span className="px-2 py-1 rounded bg-blue-100 text-blue-800">{profile?.role}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Contact:</span>
                    <span>{profile?.contact}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-semibold w-32">Status:</span>
                    <span>{profile?.status || 'Active'}</span>
                  </div>
                </div>
                <button
                  className="mt-8 w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 font-semibold transition"
                  onClick={() => setShowPasswordModal(true)}
                >
                  Change Password
                </button>
              </div>
              {/* Change Password Modal */}
              {showPasswordModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm relative">
                    <button
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl font-bold"
                      onClick={() => setShowPasswordModal(false)}
                    >
                      &times;
                    </button>
                    <h3 className="text-xl font-bold mb-4 text-center">Change Password</h3>
                    <div className="mb-4">
                      <label className="block font-semibold mb-1">Current Password</label>
                      <input
                        type="password"
                        className="border p-2 rounded w-full"
                        value={oldPassword}
                        onChange={e => setOldPassword(e.target.value)}
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block font-semibold mb-1">New Password</label>
                      <input
                        type="password"
                        className="border p-2 rounded w-full"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        minLength={6}
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label className="block font-semibold mb-1">Confirm Password</label>
                      <input
                        type="password"
                        className="border p-2 rounded w-full"
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        minLength={6}
                        required
                      />
                    </div>
                    {passwordError && (
                      <div className="text-red-600 mb-2">{passwordError}</div>
                    )}
                    <button
                      className={`bg-blue-600 text-white px-4 py-2 rounded w-full font-semibold ${passwordLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={async () => {
                        setPasswordError("");
                        if (!oldPassword) {
                          setPasswordError("Please enter your current password.");
                          return;
                        }
                        if (newPassword.length < 6) {
                          setPasswordError("Password must be at least 6 characters.");
                          return;
                        }
                        if (newPassword !== confirmPassword) {
                          setPasswordError("Passwords do not match.");
                          return;
                        }
                        setPasswordLoading(true);
                        try {
                          const res = await fetch(`${API_BASE_URL}/users/change-password`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              userId: profile?.id,
                              oldPassword,
                              newPassword
                            })
                          });
                          const data = await res.json();
                          if (data.success) {
                            setShowPasswordModal(false);
                            setOldPassword("");
                            setNewPassword("");
                            setConfirmPassword("");
                            setPasswordError("");
                          } else {
                            setPasswordError(data.message || "Failed to change password.");
                          }
                        } catch (err) {
                          setPasswordError("Server error. Please try again.");
                        }
                        setPasswordLoading(false);
                      }}
                      disabled={passwordLoading}
                    >
                      {passwordLoading ? 'Saving...' : 'Save Password'}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
          {activeSection === 'users' && (
            <section className="mt-12 flex justify-center">
              <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-7xl">
                <h2 className="text-2xl font-bold mb-6 text-center text-[#0097b2]">Assigned Users</h2>
                <table className="min-w-full bg-white rounded shadow mb-6">
                  <thead>
                    <tr>
                      <th className="px-4 py-2">ID</th>
                      <th className="px-4 py-2">Name</th>
                      <th className="px-4 py-2">Email</th>
                      <th className="px-4 py-2">Status</th>
                      <th className="px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignedUsers.map(user => (
                      <tr key={user.id} className="border-t">
                        <td className="px-4 py-2">{user.id}</td>
                        <td className="px-4 py-2">{user.first_name} {user.last_name}</td>
                        <td className="px-4 py-2">{user.email}</td>
                        <td className="px-4 py-2">{user.status || 'Active'}</td>
                        <td className="px-4 py-2 flex gap-2">
                          <button className="text-blue-600 underline" onClick={() => handleViewProfile(user)}>View Profile</button>
                          <button className="text-green-600 underline" onClick={() => handleViewDocuments(user)}>View Documents</button>
                        </td>
                      </tr>
                    ))}
                    {assignedUsers.length === 0 && (
                      <tr><td colSpan={5} className="text-center py-4">No assigned users found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Profile Modal */}
              {showProfileModal && modalUser && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm relative">
                    <button
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl font-bold"
                      onClick={() => setShowProfileModal(false)}
                    >
                      &times;
                    </button>
                    <h3 className="text-xl font-bold mb-4 text-center">User Profile</h3>
                    <div className="space-y-4">
                      <div className="flex items-center"><span className="font-semibold w-32">Username:</span><span>{modalUser.username}</span></div>
                      <div className="flex items-center"><span className="font-semibold w-32">Name:</span><span>{modalUser.first_name} {modalUser.last_name}</span></div>
                      <div className="flex items-center"><span className="font-semibold w-32">Email:</span><span>{modalUser.email}</span></div>
                      <div className="flex items-center"><span className="font-semibold w-32">Contact:</span><span>{modalUser.contact}</span></div>
                      <div className="flex items-center"><span className="font-semibold w-32">Status:</span><span>{modalUser.status || 'Active'}</span></div>
                      <div className="flex items-center"><span className="font-semibold w-32">Role:</span><span>{modalUser.role}</span></div>
                    </div>
                  </div>
                </div>
              )}
              {/* Documents Modal */}
              {showDocsModal && modalUser && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-lg relative">
                    <button
                      className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-2xl font-bold"
                      onClick={() => setShowDocsModal(false)}
                    >
                      &times;
                    </button>
                    <h3 className="text-xl font-bold mb-4 text-center">Documents for {modalUser.first_name} {modalUser.last_name}</h3>
                    <ul className="mb-4">
                      {documents.map(doc => (
                        <li key={doc.id} className="mb-2 flex justify-between items-center">
                          <span>{doc.description || doc.file_name}</span>
                          <a
                            href={`${API_BASE_URL}/documents/download/${doc.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 underline ml-2"
                          >
                            Download
                          </a>
                        </li>
                      ))}
                      {documents.length === 0 && <li>No documents uploaded.</li>}
                    </ul>
                    <div className="border-t pt-4 mt-4">
                      <h4 className="font-semibold mb-2">Upload Document</h4>
                      <input type="file" onChange={handleFileChange} />
                      <button
                        className="bg-green-600 text-white px-3 py-1 rounded mt-2"
                        onClick={handleUpload}
                        disabled={uploading || !file}
                      >
                        {uploading ? 'Uploading...' : 'Upload'}
                      </button>
                      {uploadError && <div className="text-red-600 mt-2">{uploadError}</div>}
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
          {activeSection === 'notifications' && (
            <section className="mt-12 flex justify-center">
              <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-3xl">
                <h2 className="text-2xl font-bold mb-4 text-center text-[#0097b2]">Send Notification to Assigned User</h2>
                {notifMessage && <div className="mb-4 text-center text-sm font-medium text-green-700">{notifMessage}</div>}
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block font-semibold mb-1">Select User</label>
                    <select className="border p-2 rounded w-full" value={selectedUserId ?? ''} onChange={e => {
                      const val = e.target.value;
                      if (val === 'ALL') {
                        setSendToAllAssigned(true);
                        setSelectedUserId('ALL');
                      } else {
                        setSendToAllAssigned(false);
                        setSelectedUserId(Number(val));
                      }
                    }}>
                      <option value="ALL">All assigned users</option>
                      {assignedUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.first_name} {u.last_name} &lt;{u.email}&gt;</option>
                      ))}
                      {assignedUsers.length === 0 && <option value="">No assigned users</option>}
                    </select>
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Subject</label>
                    <input className="border p-2 rounded w-full" value={notifSubject} onChange={e => setNotifSubject(e.target.value)} placeholder="Subject" />
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={sendToAllAssigned} onChange={e=>setSendToAllAssigned(e.target.checked)} /> Send to all assigned users</label>
                    {sendToAllAssigned && <div className="text-sm text-gray-600">All assigned users will receive this message.</div>}
                  </div>
                  <div>
                    <label className="block font-semibold mb-1">Message</label>
                    <textarea className="border p-2 rounded w-full h-40" value={notifText} onChange={e => setNotifText(e.target.value)} placeholder="Write your message here" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button className="bg-gray-300 text-gray-800 px-4 py-2 rounded" onClick={() => { setNotifSubject(''); setNotifText(''); setNotifMessage(null); }}>Clear</button>
                    <button className={`bg-blue-600 text-white px-4 py-2 rounded ${sendingNotif ? 'opacity-50 cursor-not-allowed' : ''}`} disabled={sendingNotif || (!sendToAllAssigned && !selectedUserId) || !notifSubject || !notifText} onClick={async () => {
                      setSendingNotif(true);
                      setNotifMessage(null);
                      setNotifProgress({sent:0,total:0});
                      try {
                        if (sendToAllAssigned) {
                          // send to all assigned users
                          const clients = assignedUsers;
                          setNotifProgress({sent:0,total:clients.length});
                          for (let i=0;i<clients.length;i++){
                            const u = clients[i];
                            const createRes = await fetch(`${API_BASE_URL}/notifications`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ purpose: 'operator_message', subject: notifSubject, text: notifText, to_email: u.email || '', is_template: false, metadata: { sender_id: profile?.id, recipient_id: u.id } })
                            });
                            const createData = await createRes.json();
                            if (createData.success && createData.notification && createData.notification.id) {
                              const nid = createData.notification.id;
                              await fetch(`${API_BASE_URL}/notifications/${nid}/send`, { method: 'POST' });
                            }
                            setNotifProgress(prev=>({sent:prev.sent+1,total:prev.total}));
                          }
                          setNotifMessage('Notifications sent to all assigned users.');
                          setNotifSubject(''); setNotifText('');
                        } else {
                          if (selectedUserId === null || selectedUserId === undefined) throw new Error('Please select a user to send to.');
                          // single user
                          const selId = selectedUserId as number;
                          const target = assignedUsers.find(a => a.id === selId);
                          if (!target) throw new Error('Selected user not found');
                          const createRes = await fetch(`${API_BASE_URL}/notifications`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ purpose: 'operator_message', subject: notifSubject, text: notifText, to_email: target.email || '', is_template: false, metadata: { sender_id: profile?.id, recipient_id: selId } })
                          });
                          const createData = await createRes.json();
                          if (!createData.success) throw new Error(createData.error || 'Failed to create notification');
                          const notifId = createData.notification.id;
                          const sendRes = await fetch(`${API_BASE_URL}/notifications/${notifId}/send`, { method: 'POST' });
                          if (!sendRes.ok) {
                            const err = await sendRes.json();
                            throw new Error(err.error || 'Failed to send notification');
                          }
                          setNotifMessage('Notification sent successfully.');
                          setNotifSubject(''); setNotifText('');
                        }
                      } catch (err:any) {
                        console.error(err);
                        setNotifMessage('Error: ' + (err.message || 'Failed to send'));
                      }
                      setSendingNotif(false);
                    }}>Send</button>
                  </div>
                  {sendingNotif && notifProgress.total > 0 && (
                    <div className="mt-2 text-sm text-gray-600">Sending {notifProgress.sent}/{notifProgress.total}...</div>
                  )}
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
